import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  TrendingUp, TrendingDown, Users, IndianRupee, CalendarCheck, Target,
  AlertTriangle, CheckCircle, Download, BarChart3, Award, Activity, FileSpreadsheet, Filter,
  Compass, Zap, Megaphone, Wallet, ShieldAlert, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const trafficLight = { green: 'bg-emerald-500', yellow: 'bg-amber-500', red: 'bg-red-500' };
const trafficBg = { green: 'bg-emerald-50 border-emerald-200', yellow: 'bg-amber-50 border-amber-200', red: 'bg-red-50 border-red-200' };
const trafficText = { green: 'text-emerald-700', yellow: 'text-amber-700', red: 'text-red-700' };

// Phase 75 — attendance heatmap cell color map
const HEATMAP_COLORS = {
  present: 'bg-emerald-500',
  absent:  'bg-red-400',
  late:    'bg-amber-400',
  leave:   'bg-slate-300',
  '':      'bg-slate-100 dark:bg-slate-800',
};

export default function KPIDashboard() {
  const [data, setData] = useState(null);
  const [heatmap, setHeatmap] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [runway, setRunway] = useState(null);
  const [riskData, setRiskData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exportingGst, setExportingGst] = useState(false);
  const [sweepingRisk, setSweepingRisk] = useState(false);

  // Phase 83 — "Why this happened" diagnostic dialog state
  const [diagOpen, setDiagOpen] = useState(false);
  const [diagLoading, setDiagLoading] = useState(false);
  const [diag, setDiag] = useState(null);

  const openDiagnose = async (student_id) => {
    setDiagOpen(true);
    setDiagLoading(true);
    setDiag(null);
    try {
      const r = await api.get(`/attendance/risk/diagnose/${student_id}`);
      setDiag(r.data);
    } catch (e) {
      toast.error('Could not load diagnosis');
      setDiagOpen(false);
    } finally {
      setDiagLoading(false);
    }
  };

  const copyMessage = () => {
    if (!diag?.suggested_message) return;
    const text = diag.suggested_message;
    const fallback = () => {
      // execCommand fallback for iframe / preview / older browsers without clipboard API
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
        toast.success('Message copied — paste into WhatsApp / SMS');
      } catch {
        toast.error('Copy unavailable here — long-press the message to select & copy');
      }
      document.body.removeChild(ta);
    };
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text)
        .then(() => toast.success('Message copied — paste into WhatsApp / SMS'))
        .catch(fallback);
    } else {
      fallback();
    }
  };

  const openWhatsAppToParent = () => {
    if (!diag?.suggested_message) return;
    const phone = (diag.parent?.phone || '').replace(/[^\d]/g, '');
    const url = phone
      ? `https://wa.me/${phone}?text=${encodeURIComponent(diag.suggested_message)}`
      : `https://wa.me/?text=${encodeURIComponent(diag.suggested_message)}`;
    window.open(url, '_blank');
  };

  useEffect(() => {
    Promise.all([
      api.get('/kpi/dashboard'),
      api.get('/kpi/attendance-heatmap?days=30').catch(() => null),
      api.get('/crm/funnel').catch(() => null),
      api.get('/finance/forecasting').catch(() => null),
      api.get('/attendance/risk/at-risk').catch(() => null),
    ]).then(([dash, hm, fn, rw, ar]) => {
      setData(dash.data);
      if (hm) setHeatmap(hm.data);
      if (fn) setFunnel(fn.data);
      if (rw) setRunway(rw.data);
      if (ar) setRiskData(ar.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const handleGstExport = async () => {
    setExportingGst(true);
    try {
      const res = await api.get('/finance/gst-export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `GST_Export_${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('GST CSV exported');
    } catch (err) {
      toast.error('GST export failed');
    } finally {
      setExportingGst(false);
    }
  };

  if (loading) return (
    <div className="space-y-6 p-6"><Skeleton className="h-8 w-64" /><div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{[1,2,3,4].map(i=><Skeleton key={i} className="h-32 rounded-xl" />)}</div></div>
  );

  if (!data) return <div className="text-center py-16 text-muted-foreground">Failed to load KPI data</div>;

  const td = data.trend_delta || {};
  const out = data.outstanding || {};

  return (
    <div className="space-y-6" data-testid="kpi-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-semibold">Executive KPI Dashboard</h1>
          <p className="text-sm text-muted-foreground">5-second business snapshot</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleGstExport} disabled={exportingGst} data-testid="kpi-gst-export-btn">
            <FileSpreadsheet className="h-4 w-4 mr-1" /> {exportingGst ? 'Generating…' : 'Export GST CSV'}
          </Button>
          <Button variant="outline" size="sm" onClick={async () => {
          if (!data) return;
          const rows = [
            ['KPI Report - ' + new Date().toLocaleDateString('en-IN')],
            [''],
            ['Metric', 'Value', 'Target', 'Status'],
            ...data.kpis.map(k => [k.name, k.value + k.unit, k.target + k.unit, k.status === 'green' ? 'On Track' : k.status === 'yellow' ? 'Needs Attention' : 'Critical']),
            [''],
            ['Summary'],
            ['Revenue Collected', data.collected],
            ['Pending', data.pending],
            ['Collection Rate', data.collection_rate + '%'],
            ['Attendance Rate', data.attendance_rate + '%'],
            ['Revenue Growth', data.revenue_growth + '%'],
            ['Active Students', data.active_students + '/' + data.total_students],
            ['Employees', data.employees],
          ];
          const csv = rows.map(r => r.join(',')).join('\n');
          const blob = new Blob([csv], { type: 'text/csv' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `KPI_Report_${new Date().toISOString().slice(0, 10)}.csv`;
          link.click();
          window.URL.revokeObjectURL(url);
          toast.success('KPI Report exported!');
        }}><Download className="h-4 w-4 mr-1" /> Export Report</Button>
        </div>
      </div>

      {/* Top-line Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="hover:shadow-md hover:-translate-y-px transition-[box-shadow,transform] duration-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted-foreground">Revenue Collected</span>
              <IndianRupee className="h-4 w-4 text-emerald-600" />
            </div>
            <p className="text-2xl font-display font-semibold tabular-nums">₹{(data.collected / 1000).toFixed(1)}K</p>
            <p className="text-xs text-muted-foreground mt-1">₹{(data.pending / 1000).toFixed(1)}K pending</p>
          </CardContent>
        </Card>
        <Card className="hover:shadow-md hover:-translate-y-px transition-[box-shadow,transform] duration-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted-foreground">Collection Rate</span>
              <Target className="h-4 w-4 text-amber-600" />
            </div>
            <p className="text-2xl font-display font-semibold tabular-nums">{data.collection_rate}%</p>
            <Progress value={data.collection_rate} className="h-1.5 mt-2" />
          </CardContent>
        </Card>
        <Card className="hover:shadow-md hover:-translate-y-px transition-[box-shadow,transform] duration-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted-foreground">Attendance</span>
              <CalendarCheck className="h-4 w-4 text-blue-600" />
            </div>
            <p className="text-2xl font-display font-semibold tabular-nums">{data.attendance_rate}%</p>
            <Progress value={data.attendance_rate} className="h-1.5 mt-2" />
          </CardContent>
        </Card>
        <Card className="hover:shadow-md hover:-translate-y-px transition-[box-shadow,transform] duration-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted-foreground">Revenue Growth</span>
              {data.revenue_growth >= 0 ? <TrendingUp className="h-4 w-4 text-emerald-600" /> : <TrendingDown className="h-4 w-4 text-red-600" />}
            </div>
            <p className={`text-2xl font-display font-semibold tabular-nums ${data.revenue_growth >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{data.revenue_growth > 0 ? '+' : ''}{data.revenue_growth}%</p>
            <p className="text-xs text-muted-foreground mt-1">vs last month</p>
          </CardContent>
        </Card>
      </div>

      {/* Traffic Light KPIs */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2"><BarChart3 className="h-4 w-4 text-primary" /> Key Performance Indicators</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {(data.kpis || []).map((kpi, i) => (
              <div key={i} className={`flex items-center justify-between p-3 rounded-lg border ${trafficBg[kpi.status]}`} data-testid={`kpi-${kpi.name.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="flex items-center gap-3">
                  <div className={`h-3 w-3 rounded-full ${trafficLight[kpi.status]}`} />
                  <div>
                    <p className="text-sm font-medium">{kpi.name}</p>
                    <p className="text-xs text-muted-foreground">Target: {kpi.target}{kpi.unit}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`text-lg font-display font-bold tabular-nums ${trafficText[kpi.status]}`}>
                    {kpi.value}{kpi.unit}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {kpi.status === 'green' ? 'On Track' : kpi.status === 'yellow' ? 'Needs Attention' : 'Critical'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-4 text-center">
            <Users className="h-5 w-5 mx-auto mb-1 text-primary" />
            <p className="text-2xl font-display font-semibold tabular-nums">{data.active_students}/{data.total_students}</p>
            <p className="text-xs text-muted-foreground">Active Students</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <IndianRupee className="h-5 w-5 mx-auto mb-1 text-emerald-600" />
            <p className="text-2xl font-display font-semibold tabular-nums">₹{(data.total_fees / 1000).toFixed(0)}K</p>
            <p className="text-xs text-muted-foreground">Total Fees Generated</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <CalendarCheck className="h-5 w-5 mx-auto mb-1 text-blue-600" />
            <p className="text-2xl font-display font-semibold tabular-nums">{data.attendance_rate}%</p>
            <p className="text-xs text-muted-foreground">Monthly Attendance</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Users className="h-5 w-5 mx-auto mb-1 text-amber-600" />
            <p className="text-2xl font-display font-semibold tabular-nums">{data.employees}</p>
            <p className="text-xs text-muted-foreground">Active Employees</p>
          </CardContent>
        </Card>
      </div>

      {/* ===== Phase 75 — Trend Delta cards (vs last month) ===== */}
      <Card data-testid="kpi-trend-delta">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2"><TrendingUp className="h-4 w-4 text-primary" /> Trends vs Last Month</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* Revenue trend */}
            {(() => {
              const r = td.revenue || {};
              const up = (r.delta_pct || 0) >= 0;
              return (
                <div className={`p-4 rounded-lg border ${up ? 'border-emerald-200 bg-emerald-50 dark:bg-emerald-950/20' : 'border-red-200 bg-red-50 dark:bg-red-950/20'}`} data-testid="trend-revenue">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-muted-foreground">Revenue</p>
                    {up ? <TrendingUp className="h-3.5 w-3.5 text-emerald-600" /> : <TrendingDown className="h-3.5 w-3.5 text-red-600" />}
                  </div>
                  <p className={`text-2xl font-display font-bold tabular-nums mt-1 ${up ? 'text-emerald-700' : 'text-red-700'}`}>
                    {up ? '+' : ''}{r.delta_pct ?? 0}%
                  </p>
                  <p className="text-[11px] text-muted-foreground mt-1">₹{(r.this_month || 0).toLocaleString()} <span className="opacity-60">vs ₹{(r.last_month || 0).toLocaleString()}</span></p>
                </div>
              );
            })()}
            {/* Students trend */}
            {(() => {
              const s = td.students || {};
              const up = (s.delta_pct || 0) >= 0;
              return (
                <div className={`p-4 rounded-lg border ${up ? 'border-emerald-200 bg-emerald-50 dark:bg-emerald-950/20' : 'border-amber-200 bg-amber-50 dark:bg-amber-950/20'}`} data-testid="trend-students">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-muted-foreground">Active Students</p>
                    {up ? <TrendingUp className="h-3.5 w-3.5 text-emerald-600" /> : <TrendingDown className="h-3.5 w-3.5 text-amber-600" />}
                  </div>
                  <p className={`text-2xl font-display font-bold tabular-nums mt-1 ${up ? 'text-emerald-700' : 'text-amber-700'}`}>
                    {up ? '+' : ''}{s.delta_pct ?? 0}%
                  </p>
                  <p className="text-[11px] text-muted-foreground mt-1">{s.this_month ?? 0} <span className="opacity-60">vs {s.last_month ?? 0}</span></p>
                </div>
              );
            })()}
            {/* Attendance trend (pp = percentage points) */}
            {(() => {
              const a = td.attendance || {};
              const up = (a.delta_pp || 0) >= 0;
              return (
                <div className={`p-4 rounded-lg border ${up ? 'border-emerald-200 bg-emerald-50 dark:bg-emerald-950/20' : 'border-red-200 bg-red-50 dark:bg-red-950/20'}`} data-testid="trend-attendance">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-muted-foreground">Attendance</p>
                    {up ? <TrendingUp className="h-3.5 w-3.5 text-emerald-600" /> : <TrendingDown className="h-3.5 w-3.5 text-red-600" />}
                  </div>
                  <p className={`text-2xl font-display font-bold tabular-nums mt-1 ${up ? 'text-emerald-700' : 'text-red-700'}`}>
                    {up ? '+' : ''}{a.delta_pp ?? 0}pp
                  </p>
                  <p className="text-[11px] text-muted-foreground mt-1">{a.this_month ?? 0}% <span className="opacity-60">vs {a.last_month ?? 0}%</span></p>
                </div>
              );
            })()}
          </div>
        </CardContent>
      </Card>

      {/* ===== Phase 82 — At-Risk Attendance (Retention Safety Net) ===== */}
      {riskData && (
        <Card className="overflow-hidden" data-testid="widget-at-risk">
          <CardContent className="p-0">
            <div className={`p-4 border-l-4 flex items-start justify-between gap-3 ${
              (riskData.at_risk || []).length > 0
                ? 'bg-gradient-to-br from-red-50 to-rose-50 dark:from-red-950/30 dark:to-rose-950/30 border-l-red-500'
                : 'bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30 border-l-emerald-500'
            }`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <ShieldAlert className={`h-4 w-4 ${(riskData.at_risk || []).length > 0 ? 'text-red-600' : 'text-emerald-600'}`} />
                  <h3 className="text-sm font-semibold">At-Risk Attendance — Retention Safety Net</h3>
                  <Badge variant="outline" className="text-[10px] ml-auto">
                    {riskData.settings.window_days}d window · &lt; {riskData.settings.threshold_pct}%
                  </Badge>
                </div>
                {(riskData.at_risk || []).length === 0 ? (
                  <p className="text-xs text-emerald-700 dark:text-emerald-300">
                    <CheckCircle className="h-3 w-3 inline mr-1" /> All active students are on track — no parents need to be alerted right now.
                  </p>
                ) : (
                  <>
                    <p className="text-xs text-red-700 dark:text-red-300 mb-2" data-testid="at-risk-summary">
                      <strong>{riskData.at_risk.length}</strong> student{riskData.at_risk.length === 1 ? '' : 's'} below {riskData.settings.threshold_pct}% attendance in the last {riskData.settings.window_days} days
                    </p>
                    <div className="space-y-1 max-h-40 overflow-y-auto" data-testid="at-risk-list">
                      {riskData.at_risk.slice(0, 6).map(s => (
                        <button
                          key={s.id}
                          onClick={() => openDiagnose(s.id)}
                          className="w-full text-left flex items-center justify-between p-2 rounded-md bg-white/70 dark:bg-slate-900/40 border border-red-100 hover:bg-white hover:shadow-sm transition-all group"
                          data-testid={`at-risk-${s.id}`}
                          title="Click to see why this student is at risk"
                        >
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-semibold truncate flex items-center gap-1">
                              {s.name}
                              <span className="text-[9px] text-red-600 group-hover:underline">Why?</span>
                            </p>
                            <p className="text-[10px] text-muted-foreground">
                              {s.custom_id || ''}{s.batch ? ` · ${s.batch}` : ''} · {s.absent} missed of {s.total}
                            </p>
                          </div>
                          <div className="text-right shrink-0 ml-2">
                            <span className="text-base font-display font-bold tabular-nums text-red-700">{s.attendance_pct}%</span>
                          </div>
                        </button>
                      ))}
                      {riskData.at_risk.length > 6 && (
                        <p className="text-[10px] text-muted-foreground text-center pt-1">+ {riskData.at_risk.length - 6} more</p>
                      )}
                    </div>
                  </>
                )}
              </div>
              <div className="flex flex-col gap-1.5 shrink-0">
                <Button
                  size="sm" variant="outline" className="h-7 text-[11px]"
                  onClick={async () => {
                    setSweepingRisk(true);
                    try {
                      const r = await api.post('/attendance/risk/sweep');
                      toast.success(`Sweep done — alerted ${r.data.alerted} parent${r.data.alerted === 1 ? '' : 's'}`);
                      const ar = await api.get('/attendance/risk/at-risk');
                      setRiskData(ar.data);
                    } catch (e) { toast.error('Sweep failed'); }
                    finally { setSweepingRisk(false); }
                  }}
                  disabled={sweepingRisk}
                  data-testid="risk-sweep-btn"
                >
                  {sweepingRisk ? <RefreshCw className="h-3 w-3 mr-1 animate-spin" /> : <Megaphone className="h-3 w-3 mr-1" />}
                  Alert Parents Now
                </Button>
                {(riskData.recent_alerts || []).length > 0 && (
                  <span className="text-[9px] text-muted-foreground text-center">
                    {riskData.recent_alerts.length} alert{riskData.recent_alerts.length === 1 ? '' : 's'} sent recently
                  </span>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ===== Phase 76 — 90-Day Cash Outlook (Financial Foresight) ===== */}
      {runway && (
        <Card className="overflow-hidden" data-testid="widget-cash-runway">
          <CardContent className="p-0">
            <div className={`p-5 text-white relative ${
              runway.health === 'green' ? 'bg-gradient-to-br from-emerald-600 via-emerald-700 to-teal-700'
              : runway.health === 'yellow' ? 'bg-gradient-to-br from-amber-500 via-amber-600 to-orange-700'
              : runway.health === 'red' ? 'bg-gradient-to-br from-red-600 via-rose-700 to-rose-800'
              : 'bg-gradient-to-br from-slate-600 via-slate-700 to-slate-800'
            }`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <Compass className="h-4 w-4 opacity-80" />
                    <p className="text-xs uppercase tracking-wider opacity-80">90-Day Cash Outlook</p>
                    <Badge className="bg-white/20 hover:bg-white/30 text-white border-0 text-[10px] tabular-nums">
                      Confidence {runway.confidence}%
                    </Badge>
                  </div>
                  <p className="text-4xl font-display font-bold tabular-nums" data-testid="cash-position-value">
                    {runway.cash_position_90d >= 0 ? '+' : '-'}₹{Math.abs(runway.cash_position_90d).toLocaleString('en-IN')}
                  </p>
                  <p className="text-xs opacity-90 mt-1">
                    Projected income ₹{(runway.projected_income_90d || 0).toLocaleString('en-IN')} − Burn ₹{(runway.projected_burn_90d || 0).toLocaleString('en-IN')}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <div className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold ${
                    runway.health === 'green' ? 'bg-white/25' :
                    runway.health === 'yellow' ? 'bg-white/25' :
                    runway.health === 'red' ? 'bg-white/25' : 'bg-white/15'
                  }`} data-testid="cash-health-badge">
                    {runway.health === 'green' ? <CheckCircle className="h-3.5 w-3.5" />
                      : runway.health === 'yellow' ? <Zap className="h-3.5 w-3.5" />
                      : runway.health === 'red' ? <AlertTriangle className="h-3.5 w-3.5" />
                      : <Wallet className="h-3.5 w-3.5" />}
                    <span className="uppercase tracking-wide">
                      {runway.health === 'green' ? 'Strong' : runway.health === 'yellow' ? 'Stable' : runway.health === 'red' ? 'At Risk' : 'Insufficient Data'}
                    </span>
                  </div>
                  {runway.burn_coverage_ratio !== null && (
                    <p className="text-[10px] opacity-80 mt-1.5">Burn coverage {(runway.burn_coverage_ratio || 0).toFixed(2)}x</p>
                  )}
                </div>
              </div>
            </div>

            {/* Stats strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border">
              <div className="bg-card p-3" data-testid="runway-monthly-income">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Monthly Income</p>
                <p className="text-base font-display font-semibold tabular-nums">₹{(runway.monthly_income || 0).toLocaleString('en-IN')}</p>
                <p className="text-[10px] text-muted-foreground">{runway.active_students || 0} active × ₹{(runway.avg_fee_per_student || 0).toLocaleString('en-IN')}</p>
              </div>
              <div className="bg-card p-3" data-testid="runway-monthly-burn">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Monthly Burn</p>
                <p className="text-base font-display font-semibold tabular-nums">₹{(runway.monthly_burn || 0).toLocaleString('en-IN')}</p>
                <p className="text-[10px] text-muted-foreground">Salaries ₹{(runway.monthly_salaries || 0).toLocaleString('en-IN')} + Rent ₹{(runway.monthly_rent || 0).toLocaleString('en-IN')}</p>
              </div>
              <div className="bg-card p-3" data-testid="runway-collection-rate">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Collection Rate</p>
                <p className="text-base font-display font-semibold tabular-nums">{runway.current_collection_rate}%</p>
                <Progress value={runway.current_collection_rate} className="h-1 mt-1.5" />
              </div>
              <div className="bg-card p-3" data-testid="runway-overdue">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Overdue</p>
                <p className="text-base font-display font-semibold tabular-nums text-red-700">₹{(runway.overdue_amount || 0).toLocaleString('en-IN')}</p>
                <p className="text-[10px] text-muted-foreground">{runway.overdue_count || 0} invoice{(runway.overdue_count || 0) === 1 ? '' : 's'}</p>
              </div>
            </div>

            {/* Recommendation */}
            {runway.recommendation && (
              <div className={`p-4 border-t flex items-center justify-between gap-3 ${
                runway.recommendation.level === 'critical' ? 'bg-red-50 dark:bg-red-950/20'
                : runway.recommendation.level === 'strong' ? 'bg-emerald-50 dark:bg-emerald-950/20'
                : runway.recommendation.level === 'stable' ? 'bg-amber-50 dark:bg-amber-950/20'
                : 'bg-slate-50 dark:bg-slate-900/40'
              }`} data-testid="runway-recommendation">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold">{runway.recommendation.headline}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{runway.recommendation.detail}</p>
                </div>
                {runway.recommendation.action_route && runway.recommendation.action_label && (
                  <Button
                    size="sm"
                    variant={runway.recommendation.level === 'critical' ? 'destructive' : 'outline'}
                    onClick={() => { window.location.href = runway.recommendation.action_route; }}
                    data-testid="runway-action-btn"
                  >
                    <Megaphone className="h-3.5 w-3.5 mr-1" />
                    {runway.recommendation.action_label}
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ===== Phase 75 — Financial Intelligence (Predictive Collections, Outstanding Receivables, Player Progress) ===== */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* Predictive Collections */}
        <Card className="overflow-hidden" data-testid="widget-predictive-collections">
          <CardContent className="p-0">
            <div className="bg-gradient-to-br from-indigo-600 to-blue-700 p-5 text-white">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs uppercase tracking-wider opacity-80">Predictive Collections</p>
                <Target className="h-4 w-4 opacity-70" />
              </div>
              <p className="text-3xl font-display font-bold tabular-nums">₹{(data.predictive_next_month || 0).toLocaleString('en-IN')}</p>
              <p className="text-xs opacity-80 mt-1">Next-month forecast</p>
            </div>
            <div className="p-3 bg-card text-xs text-muted-foreground border-t">
              {data.active_students} active × ₹{(data.avg_fee_per_student || 0).toLocaleString('en-IN')} avg fee
            </div>
          </CardContent>
        </Card>
        {/* Outstanding Receivables */}
        <Card className="overflow-hidden" data-testid="widget-outstanding-receivables">
          <CardContent className="p-0">
            <div className={`p-5 text-white bg-gradient-to-br ${(out.overdue_total || 0) > 0 ? 'from-red-600 to-rose-700' : 'from-amber-500 to-orange-600'}`}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs uppercase tracking-wider opacity-80">Outstanding Receivables</p>
                <AlertTriangle className="h-4 w-4 opacity-70" />
              </div>
              <p className="text-3xl font-display font-bold tabular-nums">₹{(out.total || 0).toLocaleString('en-IN')}</p>
              <p className="text-xs opacity-80 mt-1">{out.overdue_count || 0} overdue · {out.pending_only_count || 0} pending</p>
            </div>
            <div className="p-3 bg-card text-xs flex items-center justify-between border-t">
              <span className="text-muted-foreground">Overdue: ₹{(out.overdue_total || 0).toLocaleString('en-IN')}</span>
              <span className="text-muted-foreground">Pending: ₹{(out.pending_only_total || 0).toLocaleString('en-IN')}</span>
            </div>
          </CardContent>
        </Card>
        {/* Player Progress Average */}
        <Card className="overflow-hidden" data-testid="widget-player-progress">
          <CardContent className="p-0">
            <div className="bg-gradient-to-br from-purple-600 to-fuchsia-700 p-5 text-white">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs uppercase tracking-wider opacity-80">Player Progress Avg</p>
                <Award className="h-4 w-4 opacity-70" />
              </div>
              <p className="text-3xl font-display font-bold tabular-nums">{data.player_progress_avg || 0}%</p>
              <p className="text-xs opacity-80 mt-1">across all tracked goals</p>
            </div>
            <div className="p-3 bg-card text-xs text-muted-foreground border-t">
              {data.progress_tracked_count > 0 ? `${data.progress_tracked_count} goal${data.progress_tracked_count === 1 ? '' : 's'} tracked` : 'No goals tracked yet — set up Player Goals to populate'}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ===== Phase 75 — Attendance Heatmap (last 30 days × students) ===== */}
      {heatmap && (
        <Card data-testid="widget-attendance-heatmap">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2"><Activity className="h-4 w-4 text-primary" /> Attendance Heatmap</CardTitle>
              <div className="flex items-center gap-3 text-[10px]">
                <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-emerald-500" /> Present</span>
                <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-red-400" /> Absent</span>
                <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-amber-400" /> Late</span>
                <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-slate-300" /> Leave</span>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {heatmap.students.length === 0 ? (
              <p className="text-center py-8 text-xs text-muted-foreground">No active students to display</p>
            ) : (
              <div className="overflow-x-auto">
                <div className="inline-block min-w-full">
                  {/* Day labels (every 5 days) */}
                  <div className="flex items-end gap-px ml-32 mb-1">
                    {heatmap.days.map((d, i) => (
                      <div key={d} className="w-4 text-[8px] text-muted-foreground text-center" style={{ visibility: i % 5 === 0 ? 'visible' : 'hidden' }}>
                        {d.slice(8, 10)}
                      </div>
                    ))}
                  </div>
                  {/* Rows */}
                  {heatmap.students.slice(0, 25).map(s => (
                    <div key={s.student_id} className="flex items-center gap-px mb-px" data-testid={`heatmap-row-${s.custom_id || s.student_id}`}>
                      <div className="w-32 pr-2 text-[11px] truncate flex items-center gap-1">
                        <span className="font-medium truncate">{s.name}</span>
                      </div>
                      {s.cells.map((status, i) => (
                        <div key={i} className={`h-4 w-4 rounded-sm ${HEATMAP_COLORS[status] || HEATMAP_COLORS['']}`} title={`${heatmap.days[i]} — ${status || 'no record'}`} />
                      ))}
                    </div>
                  ))}
                  {heatmap.students.length > 25 && (
                    <p className="text-[10px] text-muted-foreground mt-2 text-center">+ {heatmap.students.length - 25} more students (scroll table below for details)</p>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ===== Phase 75 — CRM Funnel ===== */}
      {funnel && funnel.total_leads > 0 && (
        <Card data-testid="widget-crm-funnel">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><Filter className="h-4 w-4 text-primary" /> CRM Conversion Funnel</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {funnel.funnel.map((stage, i) => {
                const widthPct = funnel.funnel[0].count > 0 ? Math.max(8, (stage.count / funnel.funnel[0].count) * 100) : 8;
                const colors = ['bg-blue-500', 'bg-indigo-500', 'bg-purple-500', 'bg-fuchsia-500', 'bg-pink-500', 'bg-emerald-500'];
                return (
                  <div key={stage.status} className="flex items-center gap-3" data-testid={`funnel-${stage.status}`}>
                    <div className="w-28 text-xs font-medium">{stage.label}</div>
                    <div className="flex-1 relative h-7 bg-muted rounded">
                      <div className={`h-full ${colors[i]} rounded transition-[width] duration-500`} style={{ width: `${widthPct}%` }} />
                      <span className="absolute inset-0 flex items-center px-3 text-xs font-semibold text-white mix-blend-difference">{stage.count}</span>
                    </div>
                    {stage.conversion_pct !== null && (
                      <Badge variant="outline" className="text-[10px] tabular-nums">
                        {stage.conversion_pct >= 50 ? '✓' : ''} {stage.conversion_pct}%
                      </Badge>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t">
              <div className="text-center">
                <p className="text-2xl font-display font-bold tabular-nums">{funnel.total_leads}</p>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Total Leads</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-display font-bold tabular-nums text-emerald-700">{funnel.conversion_rate}%</p>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Conversion</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-display font-bold tabular-nums text-muted-foreground">{funnel.lost}</p>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Lost</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ===== Phase 83 — "Why this happened" Diagnostic Dialog ===== */}
      <Dialog open={diagOpen} onOpenChange={setDiagOpen}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="diag-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <ShieldAlert className="h-4 w-4 text-red-600" />
              Why this happened — {diag?.student?.name || 'Student'}
            </DialogTitle>
            <DialogDescription className="text-xs">Conversational context for the parent call. Patterns from the last {diag?.lookback_days || 30} days.</DialogDescription>
          </DialogHeader>

          {diagLoading ? (
            <div className="space-y-3 py-3">
              <Skeleton className="h-16" />
              <Skeleton className="h-16" />
              <Skeleton className="h-32" />
            </div>
          ) : diag ? (
            <div className="space-y-4">
              {/* Hero summary */}
              <div className="grid grid-cols-4 gap-2" data-testid="diag-summary">
                <div className="rounded-lg bg-red-50 dark:bg-red-950/30 p-3 text-center">
                  <p className="text-[10px] uppercase text-red-700">Attendance</p>
                  <p className="text-2xl font-display font-bold tabular-nums text-red-700">{diag.summary.attendance_pct}%</p>
                </div>
                <div className="rounded-lg bg-emerald-50 dark:bg-emerald-950/30 p-3 text-center">
                  <p className="text-[10px] uppercase text-emerald-700">Present</p>
                  <p className="text-2xl font-display font-bold tabular-nums text-emerald-700">{diag.summary.present}</p>
                </div>
                <div className="rounded-lg bg-rose-50 dark:bg-rose-950/30 p-3 text-center">
                  <p className="text-[10px] uppercase text-rose-700">Absent</p>
                  <p className="text-2xl font-display font-bold tabular-nums text-rose-700">{diag.summary.absent}</p>
                </div>
                <div className="rounded-lg bg-slate-50 dark:bg-slate-900/40 p-3 text-center">
                  <p className="text-[10px] uppercase text-slate-700">Total Marked</p>
                  <p className="text-2xl font-display font-bold tabular-nums">{diag.summary.total}</p>
                </div>
              </div>

              {/* Insights */}
              <div className="space-y-2" data-testid="diag-insights">
                <p className="text-[10px] uppercase font-bold text-muted-foreground tracking-wider">What we noticed</p>
                {diag.insights.map((ins, i) => {
                  const toneMap = {
                    critical: 'bg-red-50 dark:bg-red-950/20 border-red-200 text-red-900',
                    warning:  'bg-amber-50 dark:bg-amber-950/20 border-amber-200 text-amber-900',
                    info:     'bg-blue-50 dark:bg-blue-950/20 border-blue-200 text-blue-900',
                    positive: 'bg-emerald-50 dark:bg-emerald-950/20 border-emerald-200 text-emerald-900',
                    neutral:  'bg-slate-50 dark:bg-slate-900/40 border-slate-200 text-slate-700',
                  };
                  return (
                    <div key={i} className={`rounded-lg border p-3 ${toneMap[ins.tone] || toneMap.neutral}`} data-testid={`insight-${i}`}>
                      <p className="text-sm font-semibold">{ins.headline}</p>
                      <p className="text-xs opacity-90 mt-0.5">{ins.detail}</p>
                    </div>
                  );
                })}
              </div>

              {/* Day-of-week sparkline */}
              <div data-testid="diag-dow">
                <p className="text-[10px] uppercase font-bold text-muted-foreground tracking-wider mb-2">Day-of-week pattern</p>
                <div className="grid grid-cols-7 gap-1.5">
                  {diag.day_of_week.map((d, i) => {
                    const color = d.pct === null ? 'bg-slate-100' :
                      d.pct >= 80 ? 'bg-emerald-500' :
                      d.pct >= 60 ? 'bg-blue-400' :
                      d.pct >= 40 ? 'bg-amber-400' : 'bg-red-500';
                    return (
                      <div key={i} className="flex flex-col items-center" data-testid={`dow-${d.day}`}>
                        <div className="w-full h-16 bg-slate-100 dark:bg-slate-800 rounded relative overflow-hidden flex items-end">
                          <div className={`w-full ${color} transition-all`} style={{ height: d.pct === null ? '0%' : `${d.pct}%` }} />
                        </div>
                        <p className="text-[9px] font-semibold mt-1">{d.day}</p>
                        <p className="text-[9px] text-muted-foreground tabular-nums">{d.pct === null ? '—' : `${d.pct}%`}</p>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Timeline strip */}
              <div data-testid="diag-timeline">
                <p className="text-[10px] uppercase font-bold text-muted-foreground tracking-wider mb-2">Recent timeline ({diag.timeline.length} days marked)</p>
                <div className="flex flex-wrap gap-0.5">
                  {diag.timeline.map((t, i) => {
                    const color = t.status === 'present' ? 'bg-emerald-500'
                      : t.status === 'half_day' ? 'bg-amber-400'
                      : t.status === 'leave' ? 'bg-blue-400'
                      : t.status === 'absent' ? 'bg-red-500'
                      : 'bg-slate-300';
                    return (
                      <div
                        key={i}
                        className={`w-3 h-3 rounded ${color} hover:scale-150 transition-transform`}
                        title={`${t.date} · ${t.status}${t.marked_by_name ? ' (by ' + t.marked_by_name + ')' : ''}`}
                      />
                    );
                  })}
                </div>
                <div className="flex gap-3 text-[9px] text-muted-foreground mt-2">
                  <span className="flex items-center gap-1"><div className="w-2 h-2 bg-emerald-500 rounded-sm" /> Present</span>
                  <span className="flex items-center gap-1"><div className="w-2 h-2 bg-red-500 rounded-sm" /> Absent</span>
                  <span className="flex items-center gap-1"><div className="w-2 h-2 bg-amber-400 rounded-sm" /> Half-day</span>
                  <span className="flex items-center gap-1"><div className="w-2 h-2 bg-blue-400 rounded-sm" /> Leave</span>
                </div>
              </div>

              {/* Suggested message + action buttons */}
              <div className="rounded-lg bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30 border border-emerald-200 p-3" data-testid="diag-suggested">
                <p className="text-[10px] uppercase font-bold text-emerald-700 tracking-wider mb-1.5 flex items-center gap-1">
                  <Megaphone className="h-3 w-3" /> Suggested message to parent
                </p>
                <p className="text-xs italic text-slate-700 dark:text-slate-300 mb-3 leading-relaxed">"{diag.suggested_message}"</p>
                <div className="flex flex-wrap gap-1.5">
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-7 text-[11px]" onClick={openWhatsAppToParent} data-testid="diag-send-whatsapp">
                    <Megaphone className="h-3 w-3 mr-1" /> Open WhatsApp{diag.parent?.phone ? ` to ${diag.parent.phone}` : ''}
                  </Button>
                  <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={copyMessage} data-testid="diag-copy">
                    Copy Message
                  </Button>
                  {diag.parent?.email && (
                    <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => {
                      window.location.href = `mailto:${diag.parent.email}?subject=${encodeURIComponent(`Quick check-in about ${diag.student.name}`)}&body=${encodeURIComponent(diag.suggested_message)}`;
                    }} data-testid="diag-send-email">
                      Email Parent
                    </Button>
                  )}
                </div>
                {(diag.parent?.email || diag.parent?.phone) && (
                  <p className="text-[10px] text-muted-foreground mt-2">
                    Parent on file: {diag.parent.name || '—'}{diag.parent.email ? ` · ${diag.parent.email}` : ''}{diag.parent.phone ? ` · ${diag.parent.phone}` : ''}
                  </p>
                )}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
